import React from "react";

/**
 * The published outcome of a card photo challenge (P1-13).
 *
 * ONE component renders this signal on every surface — the card profile modal, a
 * trade's contents, the map sidebar tile and the owner's own collection — because
 * the wording is constrained by docs/CARD_VERIFICATION_BADGE_POLICY.md and copy
 * that is duplicated across four files is copy that drifts out of policy in three
 * of them.
 *
 * WHAT THIS CLAIMS, and nothing more: an admin looked at photo evidence submitted
 * with a one-time code, for this specific copy, on a date. The policy's allowed
 * wording is "Challenge evidence reviewed on <date>", and the word "verified" is
 * forbidden outright — an admin checking that a photo satisfies a challenge is
 * not an authenticity finding, and must never read like one. It says nothing
 * about whether the card is genuine, and nothing about grading or condition.
 *
 * The badge belongs to a UserCard — one owned copy — never to a Pokemon card.
 */

/** Kept in step with CHALLENGE_BADGE_TTL_DAYS in server/models/UserCard.js. */
export const CHALLENGE_BADGE_TTL_DAYS = 90;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Is this review still inside the publication window?
 *
 * The server already strips a stale badge before it is serialized, so in practice
 * this rarely fires. It is checked again here anyway: a cached response, an
 * optimistic local update or a page left open across the boundary would each
 * otherwise keep showing evidence the backend has already stopped publishing, and
 * failing closed on a trust signal costs nothing.
 */
export const isChallengeReviewCurrent = (reviewedAt, now = Date.now()) => {
  if (!reviewedAt) return false;
  const at = new Date(reviewedAt).getTime();
  if (Number.isNaN(at)) return false;
  return now - at < CHALLENGE_BADGE_TTL_DAYS * DAY_MS;
};

const formatReviewDate = (date) => {
  try {
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (_error) {
    return date.toISOString().slice(0, 10);
  }
};

const TONE = {
  fresh: { fg: "#047857", bg: "rgba(4,120,87,0.10)", border: "rgba(4,120,87,0.22)" },
  ageing: { fg: "#a16207", bg: "rgba(161,98,7,0.10)", border: "rgba(161,98,7,0.24)" },
};

/**
 * @param {string|Date|null} reviewedAt  `UserCard.challengeReviewedAt` from the API.
 * @param {'compact'|'full'} variant     `compact` for a card tile, `full` elsewhere.
 * @param {number} [now]                 Injectable clock, for tests.
 */
const ChallengeReviewedBadge = ({ reviewedAt, variant = "full", now = Date.now(), testId }) => {
  if (!isChallengeReviewCurrent(reviewedAt, now)) return null;

  const date = new Date(reviewedAt);
  const ageDays = Math.max(0, Math.floor((now - date.getTime()) / DAY_MS));
  const dateLabel = formatReviewDate(date);

  // Age is shown, never flattened. Evidence from 80 days ago is a much weaker
  // statement than evidence from yesterday, and a badge that looked identical
  // across the whole 90-day window would hide exactly the difference a
  // counterparty needs in order to judge it.
  const ageLabel =
    ageDays === 0 ? "today" : ageDays === 1 ? "yesterday" : `${ageDays} days ago`;
  const tone = ageDays > 30 ? TONE.ageing : TONE.fresh;

  // The full policy-allowed sentence is always the accessible name, even in the
  // compact variant where the visible text has to be short.
  const fullLabel = `Challenge evidence reviewed on ${dateLabel}`;

  return (
    <span
      data-testid={testId || "challenge-reviewed-badge"}
      data-review-age-days={ageDays}
      title={`${fullLabel} (${ageLabel}). An admin reviewed photo evidence submitted for this copy with a one-time code.`}
      aria-label={fullLabel}
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: 4,
        maxWidth: "100%",
        padding: variant === "compact" ? "1px 5px" : "2px 8px",
        borderRadius: 999,
        border: `1px solid ${tone.border}`,
        background: tone.bg,
        color: tone.fg,
        fontSize: variant === "compact" ? 9 : 10.5,
        fontWeight: 800,
        lineHeight: 1.5,
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <span>Challenge evidence reviewed</span>
      <span style={{ fontWeight: 600, opacity: 0.85 }}>
        {variant === "compact" ? dateLabel : `on ${dateLabel} · ${ageLabel}`}
      </span>
    </span>
  );
};

export default ChallengeReviewedBadge;
